import { track } from '@omnea-app/fe-analytics/useAnalytics'
import { isCustomBreakdownKey } from '@omnea-app/insights/customBreakdown'
import { type BreakdownBucketEntry } from '@omnea-app/insights/widgetConfig'
import { resolveBreakdowns, type WidgetConfig } from './types/widgetTypes'
import { breakdownAnalyticsKey, countCustomFilters } from './utils/customBreakdownPolicy'
import type { Dashboard } from './hooks/useDashboards'
import type { DashboardOrgAccess } from './hooks/useUpdateDashboardOrgAccess'
import type { PromptSuggestionAction } from '@omnea-app/fe-chat/ChatEmptyState'
import type { ExportFormat, FilterScope } from '@omnea-app/fe-tables/export/tableExportTypes'
import type { SchemaTableViewVisibility } from '@omnea-app/fe-tables/schema-table/types'

/** The share level an analytics event names, by what it lets someone do. */
type DashboardGrantRole = 'viewer' | 'editor'

type ShareDialogSource = 'dashboard' | 'catalog'

// The undo triggers are the user reversing a copy, not pruning a widget — analysis nets them off
// DashboardWidgetDuplicated / DashboardWidgetSavedAsNew rather than counting them as intentional
// deletions.
type WidgetDeleteTrigger = 'widget-menu' | 'duplicate-undo' | 'save-as-new-undo'

export type InsightsChatSurface = 'dashboard' | 'widget' | 'new-widget'

declare module '@omnea-app/packages/universal/analytics' {
  interface AnalyticsEvents {
    CustomDashboardHomepageViewed: Record<string, never>
    CustomDashboardOpened: {
      dashboardId: string
      widgetCount: number
      visibility: Dashboard['visibility']
      userRole: Dashboard['userRole']
    }
    CreateCustomDashboardClicked: Record<string, never>
    CustomDashboardDuplicated: {
      dashboardId: string
      newDashboardId: string
      visibility: Dashboard['visibility']
      userRole: Dashboard['userRole']
    }
    CustomDashboardDeleted: { dashboardId: string; visibility: string }
    DashboardWidgetOpened: { dashboardId: string; widgetId: string; source: string; chartType: string }
    DashboardWidgetReordered: {
      dashboardId: string
      widgetId: string
      fromIndex: number
      toIndex: number
      userRole: Dashboard['userRole']
    }
    DashboardWidgetDeleted: {
      dashboardId: string
      widgetId: string
      source: string
      chartType: string
      userRole: Dashboard['userRole']
      trigger: WidgetDeleteTrigger
    }
    DashboardWidgetDuplicated: {
      dashboardId: string
      widgetId: string
      source: string
      chartType: string
      destinationType: 'current' | 'existing' | 'new'
      userRole: Dashboard['userRole']
    }
    DashboardWidgetSavedAsNew: {
      dashboardId: string
      widgetId: string
      source: string
      chartType: string
      destinationType: 'current' | 'existing' | 'new'
      userRole: Dashboard['userRole']
    }
    AddWidgetClicked: { dashboardId: string }
    DashboardTimeFilterChanged: { dashboardId: string; timeRange: string }
    WidgetSourceSelected: { source: string }
    WidgetMetricSelected: { source: string; metricGroup: string; metricKey: string; selected: boolean }
    WidgetBreakdownChanged: { source: string; breakdownKey: string; index: number }
    WidgetBreakdownAdded: { source: string; breakdownCount: number }
    WidgetFilterChanged: { source: string; filterKey: string; index: number }
    WidgetFilterAdded: { source: string; filterCount: number }
    WidgetDrillDownClicked: { source: string; metricKey: string }
    WidgetChartTypeChanged: { source: string; chartType: string }
    WidgetTooltipViewed: { source: string; context: string }
    CustomDashboardOnboardingShown: Record<string, never>
    CustomDashboardOnboardingNextClicked: { step: number; isLastStep: boolean }
    CustomDashboardWhatsNewClicked: Record<string, never>
    CustomDashboardHomeBannerClicked: Record<string, never>
    ShareDashboardDialogOpened: {
      dashboardId: string
      source: ShareDialogSource
      visibility: Dashboard['visibility']
      userRole: Dashboard['userRole']
      sharedWithCount: number
    }
    DashboardAccessGranted: { dashboardId: string; role: DashboardGrantRole; granteeCount: number }
    DashboardAccessRoleChanged: { dashboardId: string; fromRole: DashboardGrantRole; toRole: DashboardGrantRole }
    DashboardAccessRemoved: { dashboardId: string; role: DashboardGrantRole }
    DashboardOrgAccessChanged: { dashboardId: string; fromOrgAccess: DashboardOrgAccess; toOrgAccess: DashboardOrgAccess }
    DashboardShareLinkCopied: { dashboardId: string }
    InsightsChatOpened: { surface: InsightsChatSurface; dashboardId: string; widgetId?: string }
    InsightsChatStarterSelected: {
      surface: InsightsChatSurface
      group: number
      starter: string
      action: PromptSuggestionAction
    }
    InsightsChatMessageSent: { conversationId: string }
    InsightsChatWidgetApplied: { conversationId: string }
    InsightsChatWidgetUndone: { conversationId: string }
    InsightsChatChartShown: { conversationId: string }
    InsightsChatWidgetSaveRequested: { conversationId: string; saved: boolean }
    InsightsChatQuestionsAnswered: { conversationId: string; answered: number; skipped: number }
    DashboardWidgetSaved: {
      dashboardId: string
      widgetId: string
      isNew: boolean
      source: string
      chartType: string
      breakdownKeys: string[]
      customBreakdownCount: number
      customFilterCount: number
      hasNumericBuckets: boolean
      hasDateGrouping: boolean
    }
    AddCustomBreakdownClicked: { source: string }
    CustomBreakdownImported: { source: string; questionType: string; isMultiLevel: boolean }
    WidgetBucketingCustomised: { source: string; breakdownKey: string; kind: BreakdownBucketEntry['kind'] }
    DrillDownViewCreated: { source: string; visibility: SchemaTableViewVisibility }
    DrillDownViewApplied: { source: string; visibility: SchemaTableViewVisibility; isOwner: boolean }
    DrillDownExported: { source: string; format: ExportFormat; scope: FilterScope; rowCount: number }
  }
}

export const customDashboardAnalytics = {
  homepageViewed: () => track('CustomDashboardHomepageViewed', {}),
  dashboardOpened: (props: {
    dashboardId: string
    widgetCount: number
    visibility: Dashboard['visibility']
    userRole: Dashboard['userRole']
  }) => track('CustomDashboardOpened', props),
  createDashboardClicked: () => track('CreateCustomDashboardClicked', {}),
  dashboardDuplicated: (props: {
    dashboardId: string
    newDashboardId: string
    visibility: Dashboard['visibility']
    userRole: Dashboard['userRole']
  }) => track('CustomDashboardDuplicated', props),
  dashboardDeleted: (props: { dashboardId: string; visibility: string }) => track('CustomDashboardDeleted', props),
  widgetOpened: (props: { dashboardId: string; widgetId: string; source: string; chartType: string }) =>
    track('DashboardWidgetOpened', props),
  widgetReordered: (props: {
    dashboardId: string
    widgetId: string
    fromIndex: number
    toIndex: number
    userRole: Dashboard['userRole']
  }) => track('DashboardWidgetReordered', props),
  widgetDeleted: (props: {
    dashboardId: string
    widgetId: string
    source: string
    chartType: string
    userRole: Dashboard['userRole']
    trigger: WidgetDeleteTrigger
  }) => track('DashboardWidgetDeleted', props),
  widgetDuplicated: (props: {
    dashboardId: string
    widgetId: string
    source: string
    chartType: string
    destinationType: 'current' | 'existing' | 'new'
    userRole: Dashboard['userRole']
  }) => track('DashboardWidgetDuplicated', props),
  widgetSavedAsNew: (props: {
    dashboardId: string
    widgetId: string
    source: string
    chartType: string
    destinationType: 'current' | 'existing' | 'new'
    userRole: Dashboard['userRole']
  }) => track('DashboardWidgetSavedAsNew', props),
  addWidgetClicked: (props: { dashboardId: string }) => track('AddWidgetClicked', props),
  timeFilterChanged: (props: { dashboardId: string; timeRange: string }) => track('DashboardTimeFilterChanged', props),
  sourceSelected: (props: { source: string }) => track('WidgetSourceSelected', props),
  metricSelected: (props: { source: string; metricGroup: string; metricKey: string; selected: boolean }) =>
    track('WidgetMetricSelected', props),
  breakdownChanged: (props: { source: string; breakdownKey: string; index: number }) => track('WidgetBreakdownChanged', props),
  breakdownAdded: (props: { source: string; breakdownCount: number }) => track('WidgetBreakdownAdded', props),
  filterChanged: (props: { source: string; filterKey: string; index: number }) => track('WidgetFilterChanged', props),
  filterAdded: (props: { source: string; filterCount: number }) => track('WidgetFilterAdded', props),
  drillDownClicked: (props: { source: string; metricKey: string }) => track('WidgetDrillDownClicked', props),
  chartTypeChanged: (props: { source: string; chartType: string }) => track('WidgetChartTypeChanged', props),
  tooltipViewed: (props: { source: string; context: string }) => track('WidgetTooltipViewed', props),
  onboardingShown: () => track('CustomDashboardOnboardingShown', {}),
  onboardingNextClicked: (props: { step: number; isLastStep: boolean }) => track('CustomDashboardOnboardingNextClicked', props),
  whatsNewClicked: () => track('CustomDashboardWhatsNewClicked', {}),
  homeBannerClicked: () => track('CustomDashboardHomeBannerClicked', {}),
  shareDialogOpened: (props: {
    dashboardId: string
    source: ShareDialogSource
    visibility: Dashboard['visibility']
    userRole: Dashboard['userRole']
    sharedWithCount: number
  }) => track('ShareDashboardDialogOpened', props),
  accessGranted: (props: { dashboardId: string; role: DashboardGrantRole; granteeCount: number }) =>
    track('DashboardAccessGranted', props),
  accessRoleChanged: (props: { dashboardId: string; fromRole: DashboardGrantRole; toRole: DashboardGrantRole }) =>
    track('DashboardAccessRoleChanged', props),
  accessRemoved: (props: { dashboardId: string; role: DashboardGrantRole }) => track('DashboardAccessRemoved', props),
  orgAccessChanged: (props: { dashboardId: string; fromOrgAccess: DashboardOrgAccess; toOrgAccess: DashboardOrgAccess }) =>
    track('DashboardOrgAccessChanged', props),
  shareLinkCopied: (props: { dashboardId: string }) => track('DashboardShareLinkCopied', props),
  insightsChatOpened: (props: { surface: InsightsChatSurface; dashboardId: string; widgetId?: string }) =>
    track('InsightsChatOpened', props),
  insightsChatStarterSelected: (props: {
    surface: InsightsChatSurface
    group: number
    starter: string
    action: PromptSuggestionAction
  }) => track('InsightsChatStarterSelected', props),
  insightsChatMessageSent: (props: { conversationId: string }) => track('InsightsChatMessageSent', props),
  insightsChatWidgetApplied: (props: { conversationId: string }) => track('InsightsChatWidgetApplied', props),
  insightsChatWidgetUndone: (props: { conversationId: string }) => track('InsightsChatWidgetUndone', props),
  insightsChatChartShown: (props: { conversationId: string }) => track('InsightsChatChartShown', props),
  // The editor's own widgetSaved fires too; this marks that the chat asked, and what the editor answered.
  insightsChatWidgetSaveRequested: (props: { conversationId: string; saved: boolean }) =>
    track('InsightsChatWidgetSaveRequested', props),
  // Answers to the questions the chat asked: chosen or typed, and skipped. Closing the questions sends nothing, so isn't counted.
  insightsChatQuestionsAnswered: (props: { conversationId: string; answered: number; skipped: number }) =>
    track('InsightsChatQuestionsAnswered', props),
  // A custom breakdown key names the org's form question, so only its analytics form leaves the app.
  widgetSaved: ({
    config,
    ...props
  }: {
    dashboardId: string
    widgetId: string
    isNew: boolean
    config: WidgetConfig
    hasNumericBuckets: boolean
    hasDateGrouping: boolean
  }) => {
    const breakdownKeys = resolveBreakdowns(config)
    track('DashboardWidgetSaved', {
      ...props,
      source: config.source,
      chartType: config.chartType,
      breakdownKeys: breakdownKeys.map(breakdownAnalyticsKey),
      customBreakdownCount: breakdownKeys.filter(isCustomBreakdownKey).length,
      customFilterCount: countCustomFilters(config.filters.map((filter) => filter.breakdown)),
    })
  },
  addCustomBreakdownClicked: (props: { source: string }) => track('AddCustomBreakdownClicked', props),
  customBreakdownImported: (props: { source: string; questionType: string; isMultiLevel: boolean }) =>
    track('CustomBreakdownImported', props),
  bucketingCustomised: (props: { source: string; breakdownKey: string; kind: BreakdownBucketEntry['kind'] }) =>
    track('WidgetBucketingCustomised', props),
  drillDownViewCreated: (props: { source: string; visibility: SchemaTableViewVisibility }) =>
    track('DrillDownViewCreated', props),
  drillDownViewApplied: (props: { source: string; visibility: SchemaTableViewVisibility; isOwner: boolean }) =>
    track('DrillDownViewApplied', props),
  drillDownExported: (props: { source: string; format: ExportFormat; scope: FilterScope; rowCount: number }) =>
    track('DrillDownExported', props),
}
